import axios from "axios";
import React, { useEffect, useState, useRef } from "react";
import {
  OVERALL_REPORTS_LIST,
  USER_LIST_DROPDOWN,
  COUNTRY_LIST_DROPDOWN,
  STATE_LIST_DROPDOWN,
  CHAPTER_LIST_DROPDOWN,
  REGION_LIST_DROPDOWN,
} from "../../routes/routes";
import { PAGINATE, NOPAGINATE } from "../../constant/constant";
import { NoDataFound, Pagination, hasRole } from "../../components/Utils";
import { usePermission } from "../../hooks/usePermission";
import Select from "react-select";
import { toast } from "react-toastify";
import moment from "moment";
import Excel from "exceljs";
import { saveAs } from "file-saver";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/themes/material_red.css";
import Loader from "../../components/common/Loader"; // Import the loader component
import { Link } from "react-router-dom";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

const OverallReportsList = () => {
  const { hasPermission } = usePermission();
  // ## State Variable Declaration
  const [data, setData] = useState([]);
  const [paginator, setPaginator] = useState({});
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [filter, setFilter] = useState([]);
  const [userOptions, setUserOptions] = useState({});
  const [btnExcelDisabled, setBtnExcelDisabled] = useState(false);
  const [btnPdfDisabled, setBtnPdfDisabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [countryOptions, setCountryOptions] = useState({});
  const [stateOptions, setStateOptions] = useState({});
  const [regionOptions, setRegionOptions] = useState({});
  const [chapterOptions, setChapterOptions] = useState({});
  const [showClearBtn, setShowClearBtn] = useState(false);
  const refFlatpickr = useRef(null);

  const selectStateRef = useRef(null);
  const selectRegionRef = useRef(null);
  const selectChapterRef = useRef(null);

  const handleStateClear = () => {
    selectStateRef.current.clearValue();
    selectRegionRef.current.clearValue();
    selectChapterRef.current.clearValue();
  };

  const handleRegionClear = () => {
    selectRegionRef.current.clearValue();
    selectChapterRef.current.clearValue();
  };

  const handleChapterClear = () => {
    selectChapterRef.current.clearValue();
  };

  //excel export start
  const columns = [{ header: "Sl.No.", key: "sno" }];
  if (hasRole([1])) {
    columns.push({ header: "Country", key: "country_name" });
  }
  if (hasRole([1, 6])) {
    columns.push({ header: "State", key: "state_name" });
  }
  if (hasRole([1, 6, 7])) {
    columns.push({ header: "Region", key: "region_name" });
  }
  if (hasRole([1, 6, 7, 2])) {
    columns.push({ header: "Chapter", key: "chapter_name" });
  }
  columns.push({ header: "Member", key: "name" });
  columns.push({ header: "Biz Received", key: "total_tyfcb_amount" });
  columns.push({ header: "Biz Given", key: "total_biz_given_amount" });
  columns.push({ header: "121s", key: "total_one2one_count" });
  columns.push({ header: "RFLs", key: "total_referrals_count" });
  columns.push({
    header: "General Visitors",
    key: "total_general_visitors_count",
  });
  columns.push({ header: "Chief Guests", key: "total_chief_guests_count" });
  columns.push({ header: "Power Date", key: "total_power_dates_count" });
  columns.push({ header: "Inductions", key: "total_inductions_count" });
  columns.push({ header: "Consumers", key: "total_consumers_count" });
  columns.push({ header: "Total Visitors", key: "total_visitors_count" });

  //excel title starts
  const { from_date, to_date } = filter;
  let workSheetName = `Overall Members Reports`;
  if (from_date && to_date) {
    if (from_date === to_date) {
      workSheetName += ` (${moment(from_date).format("DD/MM/YYYY")})`; // single date
    } else {
      workSheetName += ` (${moment(from_date).format("DD/MM/YYYY")} to ${moment(to_date).format("DD/MM/YYYY")})`;
    }
  } else {
    workSheetName += ` (Overall)`;
  }
  const title = workSheetName;
  const fileName = "Overall Members Reports";
  //excel title end

  const exportAsExcel = async () => {
    const workbook = new Excel.Workbook();
    try {
      // creating one worksheet in workbook
      const worksheet = workbook.addWorksheet(fileName);

      const totalCols = columns.length || 1;
      const lastColLetter = String.fromCharCode(64 + totalCols);

      // Add title row first (avoids insertRow)
      const titleRow = worksheet.addRow(new Array(totalCols).fill(""));
      titleRow.height = 20;

      // Add header row manually
      const headerRow = worksheet.addRow(columns.map((col) => col.header));
      headerRow.font = { bold: true };
      headerRow.alignment = { horizontal: "center", vertical: "middle" };

      // Set column keys and widths
      worksheet.columns.forEach((column, index) => {
        column.key = columns[index].key;
        column.width = columns[index].header.length + 15;
        column.alignment = { horizontal: "center" };
      });

      // Style title row
      worksheet.mergeCells(`A1:${lastColLetter}1`);
      const titleCell = worksheet.getCell("A1");
      titleCell.value = title || "Overall Members Reports";
      titleCell.alignment = { horizontal: "center", vertical: "middle" };
      titleCell.font = { size: 11, bold: true, color: { argb: "FFFFFF" } };
      titleCell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "019342" },
      };
      setBtnExcelDisabled(true);
      await axios
        .get(OVERALL_REPORTS_LIST, {
          params: {
            type: NOPAGINATE,
            isAdmin: 1,
            ...filter,
          },
        })
        .then(async (response) => {
          if (response.status === 200 && response.data.success) {
            const excelData = response.data.data.users;

            if (excelData.length < 1) {
              toast.error("No data found!.");
              return;
            }

            // loop through data and add each one to worksheet
            excelData.forEach((singleData) => {
              worksheet.addRow(singleData);
            });

            const overallTyfcbAmount = response.data.data.overall_tyfcb_amount;
            const overallBizGivenAmount =
              response.data.data.overall_biz_given_amount;
            const overall121Count = response.data.data.overall_one2one_count;
            const overallRFLCount = response.data.data.overall_referrals_count;
            const overallGVCount =
              response.data.data.overall_general_visitors_count;
            const overallCGCount =
              response.data.data.overall_chief_guests_count;
            const overallPDCount = response.data.data.overall_power_dates_count;
            const overallIDCount = response.data.data.overall_inductions_count;
            const overallCSCount = response.data.data.overall_consumers_count;
            const overallVCount = response.data.data.overall_visitors_count;

            // Add a total row with the total amount
            let totalRow = { sno: "" };
            if (hasRole([1])) {
              totalRow.country_name = "";
            }
            if (hasRole([1, 6])) {
              totalRow.state_name = "";
            }
            if (hasRole([1, 6, 7])) {
              totalRow.region_name = "";
            }
            if (hasRole([1, 6, 7, 2])) {
              totalRow.chapter_name = "";
            }
            totalRow.name = "Total";
            totalRow.total_tyfcb_amount = overallTyfcbAmount;
            totalRow.total_biz_given_amount = overallBizGivenAmount;
            totalRow.total_one2one_count = overall121Count;
            totalRow.total_referrals_count = overallRFLCount;
            totalRow.total_general_visitors_count = overallGVCount;
            totalRow.total_chief_guests_count = overallCGCount;
            totalRow.total_power_dates_count = overallPDCount;
            totalRow.total_inductions_count = overallIDCount;
            totalRow.total_consumers_count = overallCSCount;
            totalRow.total_visitors_count = overallVCount;

            // Add a total row with the total amount
            worksheet.addRow(totalRow);

            // Apply styles to the total row
            const lastRow = worksheet.lastRow;
            lastRow.eachCell((cell, colNumber) => {
              cell.font = { bold: true }; // Make the text bold
              cell.alignment = { horizontal: "center" }; // Align text to the right
              cell.fill = {
                type: "pattern",
                pattern: "solid",
                fgColor: { argb: "FFFFE699" }, // Light yellow background color
              };
            });

            // loop through all of the rows and set the outline style.
            worksheet.eachRow({ includeEmpty: false }, (row) => {
              // store each cell to currentCell
              const currentCell = row._cells;
              // loop through currentCell to apply border only for the non-empty cell of excel
              currentCell.forEach((singleCell) => {
                // store the cell address i.e. A1, A2, A3, B1, B2, B3, ...
                const cellAddress = singleCell._address;
                // apply border
                worksheet.getCell(cellAddress).border = {
                  top: { style: "thin" },
                  left: { style: "thin" },
                  bottom: { style: "thin" },
                  right: { style: "thin" },
                };
              });
            });

            // write the content using writeBuffer
            const buf = await workbook.xlsx.writeBuffer();
            // download the processed file
            saveAs(new Blob([buf]), `${fileName}.xlsx`);
          }
        })
        .finally(() => {
          setBtnExcelDisabled(false);
        });
    } catch (error) {
      console.error("<<<ERRROR>>>", error);
      console.error("Something Went Wrong", error.message);
      toast.error(error.message);
    } finally {
      // removing worksheet's instance to create new one
      workbook.removeWorksheet(workSheetName);
    }
  };
  //end

  const exportAsPDF = async () => {
    try {
      // Create a new PDF document
      const doc = new jsPDF("landscape");

      // Define table headers and data
      const headers = ["Sl.No."];
      if (hasRole([1])) {
        headers.push("Country");
      }
      if (hasRole([1, 6])) {
        headers.push("State");
      }
      if (hasRole([1, 6, 7])) {
        headers.push("Region");
      }
      if (hasRole([1, 6, 7, 2])) {
        headers.push("Chapter");
      }
      headers.push("Member");
      headers.push("Biz Received");
      headers.push("Biz Given");
      headers.push("121s");
      headers.push("RFLs");
      headers.push("Visitors");

      setBtnPdfDisabled(true);
      await axios
        .get(OVERALL_REPORTS_LIST, {
          params: {
            type: NOPAGINATE,
            isAdmin: 1,
            ...filter,
          },
        })
        .then(async (response) => {
          if (response.status === 200 && response.data.success) {
            if (response.data.data.users.length < 1) {
              toast.error("No data found!.");
              return;
            }

            const overallTyfcbAmount =
              response.data.data.overall_tyfcb_amount_without_format;
            const overallBizGivenAmount =
              response.data.data.overall_biz_given_amount_without_format;
            const overall121Count = response.data.data.overall_one2one_count;
            const overallRFLCount = response.data.data.overall_referrals_count;
            const overallGVCount =
              response.data.data.overall_general_visitors_count;
            const overallCGCount =
              response.data.data.overall_chief_guests_count;
            const overallPDCount = response.data.data.overall_power_dates_count;
            const overallIDCount = response.data.data.overall_inductions_count;
            const overallCSCount = response.data.data.overall_consumers_count;
            const overallVCount = response.data.data.overall_visitors_count;

            const pdfData = response.data.data.users.map((data) => {
              let pData = [data.sno];
              if (hasRole([1])) {
                pData.push(data.country_name);
              }
              if (hasRole([1, 6])) {
                pData.push(data.state_name);
              }
              if (hasRole([1, 6, 7])) {
                pData.push(data.region_name);
              }
              if (hasRole([1, 6, 7, 2])) {
                pData.push(data.chapter_name);
              }
              pData.push(data.name);
              pData.push(
                data.total_tyfcb_amount_without_format != "-"
                  ? parseFloat(
                      data.total_tyfcb_amount_without_format,
                    ).toLocaleString("en-IN")
                  : data.total_tyfcb_amount_without_format,
              );
              pData.push(
                data.total_biz_given_amount_without_format != "-"
                  ? parseFloat(
                      data.total_biz_given_amount_without_format,
                    ).toLocaleString("en-IN")
                  : data.total_biz_given_amount_without_format,
              );
              pData.push(data.total_one2one_count);
              pData.push(data.total_referrals_count);
              // pData.push(data.total_general_visitors_count);
              // pData.push(data.total_chief_guests_count);
              // pData.push(data.total_power_dates_count);
              // pData.push(data.total_inductions_count);
              // pData.push(data.total_consumers_count);
              pData.push(data.total_visitors_count);
              return pData;
            });

            let totalRowArray = [""];
            if (hasRole([1])) {
              totalRowArray.push("");
            }
            if (hasRole([1, 6])) {
              totalRowArray.push("");
            }
            if (hasRole([1, 6, 7])) {
              totalRowArray.push("");
            }
            if (hasRole([1, 6, 7, 2])) {
              totalRowArray.push("");
            }
            totalRowArray.push("Total");
            totalRowArray.push(
              overallTyfcbAmount != "-"
                ? parseFloat(overallTyfcbAmount).toLocaleString("en-IN")
                : overallTyfcbAmount,
            );
            totalRowArray.push(
              overallBizGivenAmount != "-"
                ? parseFloat(overallBizGivenAmount).toLocaleString("en-IN")
                : overallBizGivenAmount,
            );
            totalRowArray.push(overall121Count);
            totalRowArray.push(overallRFLCount);
            totalRowArray.push(overallVCount);
            pdfData.push(totalRowArray);

            //pdf title starts
            const { from_date, to_date } = filter;
            let pdfTitle = title;
            const textWidth =
              (doc.getStringUnitWidth(pdfTitle) * doc.internal.getFontSize()) /
              doc.internal.scaleFactor;
            const textOffset = (doc.internal.pageSize.width - textWidth) / 2;
            doc.text(pdfTitle, textOffset, 10); // Centered title at the top

            // Add the table to the document
            autoTable(doc, {
              head: [headers],
              body: pdfData,
              margin: { top: 20, right: 20, bottom: 20, left: 20 },
              startY: 20,
              headStyles: {
                fillColor: [1, 147, 66],
                textColor: [255, 255, 255],
              },
              styles: { lineWidth: 0.1 },
            });
            // Save the PDF
            doc.save(fileName);
          }
        })
        .finally(() => {
          setBtnPdfDisabled(false);
        });
    } catch (error) {
      console.error("<<<ERRROR>>>", error);
      console.error("Something Went Wrong", error.message);
      toast.error(error.message);
    }
  };

  const auth_user = JSON.parse(localStorage.getItem("user"));
  const isSuperAdmin = auth_user?.user_type === 1 ? 1 : 0;
  const isRegionAdmin = auth_user?.user_type === 2 ? 1 : 0;

  const handleDateRangeClear = () => {
    setShowClearBtn(false);
    refFlatpickr.current.flatpickr.setDate([]);
    setFilter({
      ...filter,
      from_date: "",
      to_date: "",
    });
  };

  useEffect(() => {
    setLoading(true);
    axios
      .get(OVERALL_REPORTS_LIST, {
        params: {
          type: PAGINATE,
          page: page,
          perPage: perPage,
          isAdmin: 1,
          ...filter,
        },
      })
      .then((response) => {
        if (response.status === 200 && response.data.success) {
          setData(response.data.data.users);
          setPaginator({
            prev: response.data.data.prev_page_url != null,
            next: response.data.data.next_page_url != null,
            total: response.data.data.total,
            current: response.data.data.current_page,
            last: response.data.data.last_page,
          });
        }
      })
      .finally(() => {
        setLoading(false); // Set loading to false after data is fetched
      });

    const fetchData = async () => {
      try {
        // Fetch user data
        const userResponse = await axios.get(USER_LIST_DROPDOWN);
        const fetchedUserOptions = userResponse.data.data;
        setUserOptions(fetchedUserOptions);

        // Fetch country data
        const countryResponse = await axios.get(COUNTRY_LIST_DROPDOWN);
        const fetchedCountryOptions = countryResponse.data.data;
        setCountryOptions(fetchedCountryOptions);

        // Fetch state data
        const stateResponse = await axios.get(STATE_LIST_DROPDOWN, {
          params: {
            ...filter,
          },
        });
        const fetchedStateOptions = stateResponse.data.data;
        setStateOptions(fetchedStateOptions);

        // Fetch region data
        const regionResponse = await axios.get(REGION_LIST_DROPDOWN, {
          params: {
            ...filter,
          },
        });
        const fetchedRegionOptions = regionResponse.data.data;
        setRegionOptions(fetchedRegionOptions);

        // Fetch chapter data
        const chapterResponse = await axios.get(CHAPTER_LIST_DROPDOWN, {
          params: {
            ...filter,
          },
        });
        const fetchedChapterOptions = chapterResponse.data.data;
        setChapterOptions(fetchedChapterOptions);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };
    fetchData();
  }, [page, filter, perPage]);

  const handleCalendarClick = () => {
    if (refFlatpickr.current) {
      refFlatpickr.current.flatpickr.open();
    }
  };

  return (
    <div className="content-wrapper">
      <div className="container-fluid p-0 mt-0 mb-3">
        <div className="col-lg-12">
          <div className="row align-items-center">
            <div className="col-lg-6">
              <h5 className="page-title">Overall Member Reports</h5>
              <ol className="breadcrumb">
                <li>
                  <div className="breadcrumb_f">
                    <div>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="50"
                        height="50"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="#212b39"
                        strokeWidth="1"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
                        <polyline points="9 22 9 12 15 12 15 22"></polyline>
                      </svg>
                    </div>
                    <div>
                      <Link to={"/dashboard"}>
                        <span>Dashboard</span>
                      </Link>
                    </div>
                  </div>
                </li>
                <li>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="50"
                    height="50"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#212b39"
                    strokeWidth="1"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M9 18l6-6-6-6" />
                  </svg>
                </li>
                <li>
                  <span>Reports</span>
                </li>
                <li>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="50"
                    height="50"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#212b39"
                    strokeWidth="1"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M9 18l6-6-6-6" />
                  </svg>
                </li>
                <li>
                  <span className="active_breadcrumb">
                    Overall Members Reports List
                  </span>
                </li>
              </ol>
            </div>
            <div className="col-lg-6">
              <div className="pt-btn">
                {hasPermission("reports-overall-list-export") && (
                  <>
                    <button
                      disabled={btnExcelDisabled}
                      type="button"
                      onClick={exportAsExcel}
                      className="btn rounded-pill btn-primary excel-btn ibutton"
                    >
                      <span className="tf-icons bx bxs-spreadsheet"></span>
                      &nbsp; Excel
                    </button>
                    <button
                      disabled={btnPdfDisabled}
                      type="button"
                      onClick={exportAsPDF}
                      className="btn rounded-pill btn-primary pdf-btn ibutton"
                    >
                      <span className="tf-icons bx bxs-spreadsheet"></span>
                      &nbsp; Download PDF
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="flex-grow-1">
        <div className="card icard">
          {/* <div className="card-header">
            <h5>Overall Members Reports</h5>
          </div> */}
          <div className="container-fluid f_containter">
            <div className="row mb-4 gx-3">
              {hasRole([1]) && (
                <div className="mb-3 col-md-3">
                  <label className="form-label" htmlFor="country_id">
                    Country
                  </label>
                  <Select
                    menuPortalTarget={document.body}
                    styles={{
                      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                    }}
                    name="country_id"
                    isClearable
                    options={countryOptions}
                    onChange={(selected) => {
                      handleStateClear();
                      setFilter({
                        ...filter,
                        country_id: selected?.value,
                        state_id: null,
                        region_id: null,
                        chapter_id: null,
                      });
                    }}
                  />
                </div>
              )}

              {hasRole([1, 6]) && (
                <div className="mb-3 col-md-3">
                  <label className="form-label" htmlFor="state_id">
                    State
                  </label>
                  <Select
                    menuPortalTarget={document.body}
                    styles={{
                      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                    }}
                    ref={selectStateRef}
                    name="state_id"
                    isClearable
                    options={stateOptions}
                    onChange={(selected) => {
                      handleRegionClear();
                      setFilter({
                        ...filter,
                        state_id: selected?.value,
                        region_id: null,
                        chapter_id: null,
                      });
                    }}
                  />
                </div>
              )}

              {hasRole([1, 6, 7]) && (
                <div className="col-md-3">
                  <div className="mb-3">
                    <label className="form-label" htmlFor="region_id">
                      Region
                    </label>
                    <Select
                      ref={selectRegionRef}
                      menuPortalTarget={document.body}
                      styles={{
                        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                      }}
                      name="region_id"
                      isClearable
                      options={regionOptions}
                      onChange={(selected) => {
                        handleChapterClear();
                        setFilter({
                          ...filter,
                          region_id: selected?.value,
                          chapter_id: null,
                        });
                      }}
                    />
                  </div>
                </div>
              )}

              {hasRole([1, 6, 7, 2]) && (
                <div className="col-md-3">
                  <div className="mb-3">
                    <label className="form-label" htmlFor="chapter_id">
                      Chapter
                    </label>
                    <Select
                      menuPortalTarget={document.body}
                      styles={{
                        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                      }}
                      ref={selectChapterRef}
                      name="chapter_id"
                      isClearable
                      options={chapterOptions}
                      onChange={(selected) =>
                        setFilter({ ...filter, chapter_id: selected?.value })
                      }
                    />
                  </div>
                </div>
              )}

              <div className="col-md-3">
                <div className="mb-3">
                  <label className="form-label" htmlFor="email">
                    Member
                  </label>
                  <Select
                    menuPortalTarget={document.body}
                    styles={{
                      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                    }}
                    name="user_id"
                    isClearable
                    options={userOptions}
                    onChange={(selected) =>
                      setFilter({ ...filter, user_id: selected?.value })
                    }
                  />
                </div>
              </div>

              <div className="col-md-3">
                <div className="mb-3 dateRangeDiv">
                  <label className="form-label" htmlFor="date_range">
                    Date Range
                  </label>
                  <div className="datepicker_wrapper">
                    <div className="datepicker">
                      <Flatpickr
                        ref={refFlatpickr}
                        className="form-control"
                        options={{
                          mode: "range",
                          dateFormat: "Y-m-d",
                          onChange: (selected) => {
                            setShowClearBtn(true);
                            if (
                              selected[0] !== undefined &&
                              selected[1] !== undefined &&
                              selected[1] !== undefined &&
                              selected[1] !== ""
                            ) {
                              setFilter({
                                ...filter,
                                from_date: selected[0]
                                  ? moment(selected[0]).format("YYYY-MM-DD")
                                  : "",
                                to_date: selected[1]
                                  ? moment(selected[1]).format("YYYY-MM-DD")
                                  : "",
                              });
                            }
                          },
                          onClose: (selected) => {
                            if (!selected[1]) {
                              setShowClearBtn(false);
                            }
                          },
                        }}
                        placeholder="Date Range"
                      />
                      {showClearBtn && (
                        <button
                          className="border-0 clearDateRangeBtn"
                          data-bs-toggle="tooltip"
                          data-bs-offset="0,4"
                          data-bs-placement="top"
                          data-bs-html="true"
                          title="Clear"
                          data-bs-original-title="Clear"
                          onClick={handleDateRangeClear}
                        >
                          <i className="bx bx-x"></i>
                        </button>
                      )}
                    </div>
                    <div className="cal_icon" onClick={handleCalendarClick}>
                      <span className="input-group-text calendar-icon"></span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* Pagination Component */}
          {data.length > 0 && (
            <Pagination
              paginator={paginator}
              page={(no) => setPage(no)}
              perPage={perPage}
              setPerPage={setPerPage}
            />
          )}
          <div className="table-responsive text-nowrap">
            <table className="table table-striped itable">
              <thead>
                <tr>
                  <th>Sl. No.</th>
                  {hasRole([1]) && <th>Country</th>}
                  {hasRole([1, 6]) && <th>State</th>}
                  {hasRole([1, 6, 7]) && <th>Region</th>}
                  {hasRole([1, 6, 7, 2]) && <th>Chapter</th>}
                  <th>Member</th>
                  <th>Biz Received</th>
                  <th>Biz Given</th>
                  <th>121s</th>
                  <th>RFLs</th>
                  <th>General Visitors</th>
                  <th>Chief Guests</th>
                  <th>Power Date</th>
                  <th>Inductions</th>
                  <th>Consumers</th>
                  <th>Total Visitors</th>
                </tr>
              </thead>
              <tbody className="table-border-bottom-0">
                {loading ? (
                  <Loader loading={loading} colspan="16" />
                ) : data.length > 0 ? (
                  data.map((user, i) => (
                    <tr key={i}>
                      <td>{user?.sno}</td>
                      {hasRole([1]) && <td>{user?.country_name}</td>}
                      {hasRole([1, 6]) && <td>{user?.state_name}</td>}
                      {hasRole([1, 6, 7]) && <td>{user?.region_name}</td>}
                      {hasRole([1, 6, 7, 2]) && <td>{user?.chapter_name}</td>}
                      <td>
                        <span className="table_img pr-2">
                          <img src={user?.profile_image} alt="icon" />
                        </span>
                        {user?.name}
                      </td>
                      <td>{user?.total_tyfcb_amount}</td>
                      <td>{user?.total_biz_given_amount}</td>
                      <td>{user?.total_one2one_count}</td>
                      <td>{user?.total_referrals_count}</td>
                      <td>{user?.total_general_visitors_count}</td>
                      <td>{user?.total_chief_guests_count}</td>
                      <td>{user?.total_power_dates_count}</td>
                      <td>{user?.total_inductions_count}</td>
                      <td>{user?.total_consumers_count}</td>
                      <td>{user?.total_visitors_count}</td>
                    </tr>
                  ))
                ) : (
                  <NoDataFound colspan="16" message="No Data Found" />
                )}
              </tbody>
            </table>
          </div>
          {/* Pagination Component */}
          {data.length > 0 && (
            <Pagination
              paginator={paginator}
              page={(no) => setPage(no)}
              perPage={perPage}
              setPerPage={setPerPage}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default OverallReportsList;
